import type { Portfolio } from "@/types/Portfolio";
import type { CategoryQuery } from "@/widget-components/newsquawk-headlines/models/CategoryQuery";
import type { HighlightInfo, ListMatch } from "@/utils/portfolioUtils";
import { combinedWatchlistAsPortfolio } from "./combinedWatchlistTerms";
import { isEmptyView, savedViewColor } from "./savedViewHighlight";
import { parseKey, type HighlightKey } from "./highlightOrder";

/**
 * Highlighting a row by every list it matches, in the widget's order.
 *
 * A row used to be painted by one list — the watchlist with the best priority,
 * or failing that the first combined list ticked — and named only that one. A
 * headline about oil that matches both "Oil" and "Energy names" now takes the
 * colour of whichever comes first in the widget, and names both: a colour
 * alone doesn't say which list it stands for once there are several.
 */

/** A highlighted list, ready to match: a watchlist, or a combined watchlist. */
export type HighlightCandidate =
  | {
      key: HighlightKey;
      kind: "watchlist";
      name: string;
      color: string;
      /** What the row emphasises in its text when this list paints it. */
      terms: string[];
      list: Portfolio;
    }
  | {
      key: HighlightKey;
      kind: "combined";
      name: string;
      color: string;
      terms: string[];
      query: CategoryQuery;
      /**
       * The list reduced to the tickers and keywords a feed without tags can
       * match — see combinedWatchlistAsPortfolio. Null when nothing survives.
       */
      flat: Portfolio | null;
    };

/** The widget's highlighted lists, in its order; lists that no longer exist drop out. */
export function buildHighlightCandidates(
  order: HighlightKey[],
  portfolios: Portfolio[] | undefined,
  queries: CategoryQuery[] | undefined
): HighlightCandidate[] {
  const candidates: HighlightCandidate[] = [];
  for (const key of order) {
    const parsed = parseKey(key);
    if (!parsed) continue;
    if (parsed.kind === "watchlist") {
      const list = portfolios?.find((p) => p.id === parsed.id);
      if (!list) continue;
      candidates.push({
        key,
        kind: "watchlist",
        name: list.name,
        color: list.color,
        terms: [...(list.tickers ?? []), ...(list.keywords ?? [])],
        list,
      });
    } else {
      const query = queries?.find((q) => q.id === parsed.id);
      if (!query) continue;
      const flat = combinedWatchlistAsPortfolio(query);
      candidates.push({
        key,
        kind: "combined",
        name: query.queryName,
        color: savedViewColor(query),
        terms: flat ? [...flat.tickers, ...(flat.keywords ?? [])] : [],
        query,
        flat,
      });
    }
  }
  return candidates;
}

/**
 * Every candidate the item matches, in order.
 *
 * Each feed family brings its own matchers — a headline carries tags, a
 * firehose story only text and tickers. An empty combined list never matches:
 * as a rule it matches everything, which would paint the whole feed.
 */
export function matchHighlights<T>(
  item: T,
  candidates: HighlightCandidate[],
  matchWatchlist: (item: T, list: Portfolio) => boolean,
  matchCombined: (item: T, query: CategoryQuery, flat: Portfolio | null) => boolean
): HighlightCandidate[] {
  return candidates.filter((candidate) =>
    candidate.kind === "watchlist"
      ? matchWatchlist(item, candidate.list)
      : !isEmptyView(candidate.query) &&
        matchCombined(item, candidate.query, candidate.flat)
  );
}

/**
 * A combined-list matcher for feeds whose items carry no tags — the firehose
 * feeds and Stock Ratings.
 *
 * A list of nothing but tags leaves such a feed nothing to match on, and the
 * firehose evaluator reads "no terms" as "match everything", which would paint
 * every row. So a list with no terms a feed can use matches nothing here.
 */
export function withTermsOnly<T>(
  match: (item: T, query: CategoryQuery) => boolean
): (item: T, query: CategoryQuery, flat: Portfolio | null) => boolean {
  return (item, query, flat) => flat !== null && match(item, query);
}

/**
 * The row's highlight: the first match paints it and lends its terms for
 * in-text emphasis; every match is named.
 */
export function toHighlightInfo(
  matches: HighlightCandidate[]
): HighlightInfo | undefined {
  const [top] = matches;
  if (!top) return undefined;
  const id = parseKey(top.key)?.id ?? 0;
  return {
    // Negative for a combined list, as combinedWatchlistAsPortfolio numbers
    // them, so the two id sequences can't be confused.
    portfolioId: top.kind === "combined" ? -id : id,
    portfolioName: top.name,
    color: top.color,
    matchedTickers: top.terms,
    matches: matches.map(({ key, name, color }): ListMatch => ({ key, name, color })),
  };
}

/**
 * The lists a highlight names. A highlight built before rows carried every
 * match (the demo feed's, say) names its one list; a row painted only by a
 * source colour names none.
 */
export function namedMatches(
  info: HighlightInfo | undefined
): Pick<ListMatch, "name" | "color">[] {
  if (!info) return [];
  if (info.matches) return info.matches;
  return info.portfolioName ? [{ name: info.portfolioName, color: info.color }] : [];
}

/** Every row paired with its highlight, if any. */
export function highlightRows<T>(
  data: T[] | undefined,
  candidates: HighlightCandidate[],
  matchWatchlist: (item: T, list: Portfolio) => boolean,
  matchCombined: (item: T, query: CategoryQuery, flat: Portfolio | null) => boolean
): [T, HighlightInfo | undefined][] {
  if (!data) return [];
  if (candidates.length === 0) return data.map((item) => [item, undefined]);
  return data.map((item) => [
    item,
    toHighlightInfo(matchHighlights(item, candidates, matchWatchlist, matchCombined)),
  ]);
}
